/**
 * meta/stations.js — the meta surfaces that live near the entrance.
 *
 * Four things, clustered by the gate so a player meets them on the way in:
 *
 *   leaderboard boards — the hub's two physical boards, cycling the twelve
 *                        stalls every few seconds (GDD 7.6)
 *   challenges panel   — this week's three Star challenges (GDD 8.2)
 *   daily panel        — the 30-day login calendar (GDD 8.7)
 *   store kiosk        — ticket packs, and ONLY when IAP is on (GDD 12.4)
 *
 * @module meta/stations
 */

import * as THREE from 'three';
import {
  HUB, STORE_CONFIG, STALL_IDS, LEADERBOARD_ROTATE_SECONDS, META_INTERACT_RADIUS,
  PALETTE, EVENTS,
} from '../constants.js';
import { bus } from '../core/events.js';
import * as factory from '../assets3d/factory.js';
import { hub } from '../world/hub.js';
import { createButtonPanel } from './panel-ui.js';
import { leaderboards } from './leaderboards.js';
import { challenges } from './challenges.js';
import { dailyLogin } from './daily-login.js';
import { store } from './store.js';

/** Where each interactive station is used from. */
export const STATION_POINTS = Object.freeze({
  store: new THREE.Vector3(STORE_CONFIG.kiosk.x, 0, STORE_CONFIG.kiosk.z - 1.6),
  daily: new THREE.Vector3(2.8, 0, 6.2),
  // Just inside the boards rather than level with them: at z = 6.0 this sits
  // 3.0 m from the spawn point, so the prompt would fire the instant the player
  // materialises and fight whatever onboarding wants to say first.
  boards: new THREE.Vector3(0, 0, HUB.leaderboards[0].z - 0.9),
});

class Stations {
  constructor() {
    /** @type {THREE.Group|null} */ this.root = null;
    /** @type {THREE.Group[]} */ this.boards = [];
    /** @type {THREE.Mesh|null} */ this.challengePanel = null;
    /** @type {THREE.Mesh|null} */ this.dailyPanel = null;
    /** @type {THREE.Mesh|null} */ this.storePanel = null;
    /** @type {THREE.Group|null} */ this.kiosk = null;
    this._rotateTimer = 0;
    this._stallIndex = 0;
    /** Last rows pushed to each physical board, by scope. */
    this._painted = { weekly: [], alltime: [] };
    this._near = { store: false, daily: false, boards: false };
  }

  /** @param {THREE.Scene} scene @returns {this} */
  build(scene) {
    if (this.root) return this;
    this.root = new THREE.Group();
    this.root.name = 'meta-stations';
    scene.add(this.root);

    this._claimBoards(scene);
    this._buildChallengePanel();
    this._buildDailyPanel();
    if (store.isEnabled()) this._buildKiosk();

    this.refresh();
    return this;
  }

  /**
   * Take over the content of the hub's two boards.
   * @private
   * @param {THREE.Scene} scene
   */
  _claimBoards(scene) {
    // Find boards by the CONTRACT rather than by name: ARCHITECTURE §8 says a
    // leaderboard board is the thing carrying `userData.setRows`. Matching on
    // `leaderboard-weekly` instead couples this to a string in hub.js, and when
    // that lookup misses it fails silently — the boards simply keep the
    // factory's "1. —" placeholders, which reads as "not implemented yet"
    // rather than as a bug.
    const root = scene || hub.root;
    if (!root) return;

    root.traverse((obj) => {
      if (typeof obj.userData?.setRows === 'function') this.boards.push(obj);
    });

    // Orientation is deliberately left alone: the factory gives each board a
    // readable face on both sides, so however the hub yaws them there is always
    // something to read. Only the content is ours.
  }

  /** @private This week's three challenges, beside the left-hand board. */
  _buildChallengePanel() {
    this.challengePanel = createButtonPanel({
      width: 1.7, height: 1.15,
      title: 'WEEKLY CHALLENGES',
    });
    this.challengePanel.position.set(-4.45, 1.7, HUB.leaderboards[0].z - 2.0);
    this.challengePanel.rotation.y = -Math.PI / 2;
    this.root.add(this.challengePanel);
  }

  /** @private The login calendar, on the way in from the gate. */
  _buildDailyPanel() {
    const stand = this._buildDailyStand();

    this.dailyPanel = createButtonPanel({
      width: 1.6, height: 1.05,
      title: 'DAILY REWARD',
    });
    // On the stand's face rather than in world space: the group carries the
    // position and the −0.25 yaw that turns the panel toward the gate.
    this.dailyPanel.position.set(0, 1.6, 0.035);
    stand.add(this.dailyPanel);
    this.dailyPanel.onSelect((id) => {
      if (id !== 'claim') return;
      dailyLogin.claim();
      this.refresh();
    });
  }

  /**
   * @private A grounded hoarding for the daily panel, which otherwise hung in
   * mid air in front of the right-hand leaderboard. Same vocabulary as those
   * boards — plum frame, gold bezel, iron legs — so it reads as one more of the
   * gate's fixtures. Sized to clear the board at x 4.5 and the gate at z 11.5.
   * @returns {THREE.Group}
   */
  _buildDailyStand() {
    const g = new THREE.Group();
    g.name = 'daily-stand';
    g.position.set(2.8, 0, 7.2);
    g.rotation.y = -0.25;

    const bezel = new THREE.Mesh(
      new THREE.BoxGeometry(1.92, 1.36, 0.06),
      factory.mat(factory.ART.gold, { rough: 0.42, metal: 0.45 }),
    );
    bezel.position.set(0, 1.6, -0.1);
    g.add(bezel);

    const board = new THREE.Mesh(
      new THREE.BoxGeometry(1.78, 1.22, 0.12),
      factory.mat(factory.ART.plumDeep, { rough: 0.82 }),
    );
    board.position.set(0, 1.6, -0.05);
    board.castShadow = true;
    g.add(board);

    const cornice = new THREE.Mesh(
      new THREE.BoxGeometry(1.98, 0.12, 0.26),
      factory.mat(factory.ART.gold, { rough: 0.42, metal: 0.45 }),
    );
    cornice.position.set(0, 2.27, -0.05);
    g.add(cornice);

    for (const s of [-1, 1]) {
      const leg = new THREE.Mesh(
        new THREE.CylinderGeometry(0.055, 0.075, 1.02, 8),
        factory.mat(factory.ART.iron, { rough: 0.45, metal: 0.6 }),
      );
      leg.position.set(0.62 * s, 0.51, -0.05);
      leg.castShadow = true;
      g.add(leg);

      const foot = new THREE.Mesh(
        new THREE.BoxGeometry(0.34, 0.09, 0.42),
        factory.mat(factory.ART.plum, { rough: 0.85 }),
      );
      foot.position.set(0.62 * s, 0.045, -0.05);
      g.add(foot);
    }

    this.root.add(g);
    return g;
  }

  /** @private A small ticket kiosk. Never built when IAP is off (GDD 12.4). */
  _buildKiosk() {
    const g = new THREE.Group();
    g.name = 'store-kiosk';
    g.position.set(STORE_CONFIG.kiosk.x, 0, STORE_CONFIG.kiosk.z);
    g.rotation.y = STORE_CONFIG.kiosk.yaw;

    const counter = new THREE.Mesh(
      new THREE.BoxGeometry(1.7, 1.05, 0.75),
      factory.mat(PALETTE.wood, { rough: 0.85 }),
    );
    counter.position.y = 0.525;
    counter.castShadow = true;
    g.add(counter);

    const top = new THREE.Mesh(
      new THREE.BoxGeometry(1.85, 0.07, 0.9),
      factory.mat(PALETTE.ticket, { rough: 0.5 }),
    );
    top.position.y = 1.09;
    g.add(top);

    for (const side of [-1, 1]) {
      const post = new THREE.Mesh(
        new THREE.CylinderGeometry(0.045, 0.045, 2.3, 8),
        factory.mat(PALETTE.metal, { rough: 0.4, metal: 0.7 }),
      );
      post.position.set(0.8 * side, 1.15, -0.3);
      g.add(post);
    }

    const canopy = new THREE.Mesh(
      new THREE.BoxGeometry(1.95, 0.1, 1.0),
      factory.mat(PALETTE.stripeA, { rough: 0.8 }),
    );
    canopy.position.set(0, 2.3, -0.1);
    g.add(canopy);

    const sign = factory.createSign('', {
      w: 1.5, h: 0.4, lines: ['TICKET KIOSK'], bg: '#1b2340', size: 44,
    });
    sign.position.set(0, 1.95, -0.32);
    sign.rotation.y = Math.PI;
    g.add(sign);

    this.root.add(g);
    this.kiosk = g;

    this.storePanel = createButtonPanel({
      width: 1.5, height: 1.0,
      title: 'TICKET PACKS',
    });
    // Sits on the counter, facing whoever walked up to it.
    this.storePanel.position.set(0, 1.6, -0.55);
    this.storePanel.rotation.y = Math.PI;
    g.add(this.storePanel);
    this.storePanel.onSelect((id) => {
      if (!id.startsWith('pack:')) return;
      store.buy(id.slice(5));
      this.refresh();
    });
  }

  // ── Content ─────────────────────────────────────────────────────────────

  /** Redraw every station from live state. */
  refresh() {
    this._refreshBoards();

    if (this.challengePanel) {
      const rows = challenges.list().map((c) => ({
        label: c.text,
        right: `${c.done ? '✓' : `${c.progress}/${c.target}`}  ${c.stars}★`,
        state: c.done ? 'owned' : 'normal',
      }));
      this.challengePanel.setContent({
        subtitle: `Week ${challenges.weekKey()}`,
        rows,
        footer: 'New challenges every Monday',
      });
    }

    if (this.dailyPanel) {
      const state = dailyLogin.state();
      const reward = state.nextReward;
      const rows = [{
        id: state.due ? 'claim' : null,
        label: state.due ? `CLAIM DAY ${state.nextDay}` : 'Claimed today',
        right: state.due && reward
          ? `+${reward.tickets}🎟${reward.stars ? ` +${reward.stars}★` : ''}`
          : '',
        state: state.due ? 'normal' : 'disabled',
      }];
      this.dailyPanel.setContent({
        subtitle: `Streak: day ${state.streak} of 30`,
        rows,
        footer: state.due ? 'Miss a day and the streak resets' : 'Come back tomorrow',
      });
    }

    if (this.storePanel) {
      const rows = store.packs().map((p) => ({
        id: `pack:${p.id}`,
        label: p.name,
        right: `${p.tickets}🎟`,
        note: `${p.priceLabel}${p.bonusLabel ? ` · ${p.bonusLabel}` : ''}`,
      }));
      this.storePanel.setContent({
        subtitle: STORE_CONFIG.demoLabel,
        rows,
        footer: 'No real payment is taken',
      });
    }
  }

  /** @private Paint the current stall onto both physical boards. */
  _refreshBoards() {
    const stallId = STALL_IDS[this._stallIndex % STALL_IDS.length];
    this.boards.forEach((board, i) => {
      const name = String(board.name || '');
      // Prefer what the board calls itself; fall back to hub order (GDD 10.2
      // puts weekly first) so an unnamed board still gets a sensible scope.
      const scope = name.includes('alltime') ? 'alltime'
        : name.includes('weekly') ? 'weekly'
          : (i === 0 ? 'weekly' : 'alltime');
      const lines = leaderboards.boardLines(stallId, scope);
      board.userData.setRows(lines);
      // Remember what was pushed. The board face is a canvas texture and cannot
      // be read back, so this is the only way a test can tell a live board from
      // one still showing the factory's placeholder rows.
      this._painted[scope] = lines;
    });
  }

  /**
   * What the physical hub boards are currently showing.
   * @returns {{ claimed: number, weekly: string[], alltime: string[] }}
   */
  boardState() {
    return {
      claimed: this.boards.length,
      weekly: [...(this._painted.weekly || [])],
      alltime: [...(this._painted.alltime || [])],
    };
  }

  /** @returns {string} The stall currently shown on the hub boards. */
  currentBoardStall() {
    return STALL_IDS[this._stallIndex % STALL_IDS.length];
  }

  /** Step the boards to the next stall. */
  nextBoardStall() {
    this._stallIndex = (this._stallIndex + 1) % STALL_IDS.length;
    this._refreshBoards();
    return this.currentBoardStall();
  }

  // ── Presence ────────────────────────────────────────────────────────────

  /**
   * @param {number} dt
   * @param {THREE.Vector3} playerPos
   */
  update(dt, playerPos) {
    if (!this.root) return;

    this._rotateTimer += dt;
    if (this._rotateTimer >= LEADERBOARD_ROTATE_SECONDS) {
      this._rotateTimer = 0;
      this.nextBoardStall();
    }

    if (!playerPos) return;
    let changed = false;
    for (const key of Object.keys(this._near)) {
      const near = playerPos.distanceTo(STATION_POINTS[key]) <= META_INTERACT_RADIUS;
      if (near === this._near[key]) continue;
      this._near[key] = near;
      changed = true;
      if (near) this.refresh();
    }
    if (!changed) return;

    // Resolve the prompt from whatever is nearest NOW. Emitting only on entry
    // leaves the last station's prompt burned onto the screen for the whole
    // walk down the midway.
    const station = this.nearestStation();
    bus.emit(EVENTS.UI_PROMPT, {
      text: station ? `${PROMPTS[station]} — press F` : '',
      show: !!station,
    });
  }

  /**
   * Show or hide the whole ticket kiosk (GDD 12.4). Toggling IAP off has to
   * remove the store from the world, not merely refuse to sell — so the kiosk
   * and its panel both go, and the panel stops being hit-testable with them.
   * @param {boolean} on
   */
  setStoreVisible(on) {
    if (on && !this.kiosk && this.root) this._buildKiosk();
    if (this.kiosk) this.kiosk.visible = on;
    if (this.storePanel) this.storePanel.visible = on;
    if (on) this.refresh();
  }

  /** @returns {boolean} Is the ticket kiosk present and visible? */
  storeVisible() {
    return !!(this.kiosk && this.kiosk.visible);
  }

  /** @returns {string|null} Which station the player is standing at, if any. */
  nearestStation() {
    for (const key of Object.keys(this._near)) if (this._near[key]) return key;
    return null;
  }

  /** @param {string} key @returns {boolean} */
  isNear(key) {
    return !!this._near[key];
  }
}

const PROMPTS = {
  store: 'Ticket Kiosk',
  daily: 'Daily Reward',
  boards: 'Leaderboards',
};

/** The one set of stations. */
export const stations = new Stations();
export default stations;
